import { CheckCircleIcon, InfoIcon, XIcon } from "@phosphor-icons/react";
import { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ToastContext, type Toast, type ToastContextValue, type ToastTone } from "./toast-context";

const DISMISS_AFTER_MS = 6000;

const icons: Record<ToastTone, typeof InfoIcon> = {
  info: InfoIcon,
  success: CheckCircleIcon,
};

let sequence = 0;

export function ToastProvider({ children }: PropsWithChildren) {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const timers = useRef(new Map<string, number>());

  const dismissToast = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, tone: ToastTone = "success") => {
      sequence += 1;
      const id = `toast-${String(sequence)}`;
      setToasts((current) => [...current, { id, message, tone }]);
      timers.current.set(
        id,
        window.setTimeout(() => dismissToast(id), DISMISS_AFTER_MS),
      );
    },
    [dismissToast],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({ dismissToast, showToast, toasts }),
    [dismissToast, showToast, toasts],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/*
        A polite live region so a routine confirmation is announced without
        interrupting what the person is doing, and never steals focus.
      */}
      <div
        aria-label="Notifications"
        aria-live="polite"
        // Clears the phone's bottom navigation bar (AppShell).
        className="pointer-events-none fixed inset-x-0 bottom-0 z-dialog flex flex-col items-center gap-2 p-4 pb-24 sm:items-end lg:pb-4"
        role="status"
      >
        {toasts.map((toast) => {
          const Icon = icons[toast.tone];
          return (
            <div
              className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-panel border border-border bg-surface-raised p-3 text-sm text-text shadow-panel"
              key={toast.id}
            >
              <Icon aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-accent" weight="fill" />
              <p className="min-w-0 flex-1 leading-6">{toast.message}</p>
              <button
                aria-label={`Dismiss notification: ${toast.message}`}
                className="grid size-12 shrink-0 place-items-center rounded-control text-text-muted hover:bg-surface-muted hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                onClick={() => dismissToast(toast.id)}
                type="button"
              >
                <XIcon aria-hidden="true" className="size-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
